// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { CodeableConcept, Communication, Observation } from '@medplum/fhirtypes';
import {
  RPM_MONTH_IDENTIFIER_SYSTEM,
  RPM_OBSERVATION_CODES,
  type RpmAsyncActivityType,
  type RpmSyncContactMethod,
} from '@polarissleep/shared';

import { RPM_NOTE_DETAIL_SYSTEM, type RpmObservationCode } from './rpmFhir';

export const RPM_NOTE_COMMUNICATION_SYSTEM = 'https://polarissleep.com/fhir/rpm-note-communication';
export const RPM_NOTE_COMMUNICATION_CODES = {
  note: 'note',
  addendum: 'addendum',
} as const;

export const RPM_NOTE_EXTENSION_URLS = {
  month: `${RPM_NOTE_COMMUNICATION_SYSTEM}/month`,
  asyncReviewed: `${RPM_NOTE_COMMUNICATION_SYSTEM}/async-reviewed`,
  syncContact: `${RPM_NOTE_COMMUNICATION_SYSTEM}/sync-contact`,
  daysReviewed: `${RPM_NOTE_COMMUNICATION_SYSTEM}/days-reviewed`,
  asyncMinutes: `${RPM_NOTE_COMMUNICATION_SYSTEM}/async-minutes`,
  asyncActivityType: `${RPM_NOTE_COMMUNICATION_SYSTEM}/async-activity-type`,
  syncMinutes: `${RPM_NOTE_COMMUNICATION_SYSTEM}/sync-minutes`,
  syncMethod: `${RPM_NOTE_COMMUNICATION_SYSTEM}/sync-method`,
  kind: `${RPM_NOTE_COMMUNICATION_SYSTEM}/kind`,
  parent: `${RPM_NOTE_COMMUNICATION_SYSTEM}/parent`,
} as const;

export interface RpmHistoryMonthRow {
  month: string;
  adherenceNights?: number;
  lastContactDate?: string;
  asyncMinutes?: number;
  syncMinutes?: number;
  monitoringStatus?: string;
  monthlyNote?: string;
}

export interface RpmMonthNoteEntry {
  id: string;
  sent?: string;
  text: string;
  author: string;
  month: string;
  asyncReviewed: boolean;
  syncContact: boolean;
  daysReviewed?: number;
  asyncMinutes?: number;
  asyncActivityType?: string;
  syncMinutes?: number;
  syncMethod?: string;
  kind: 'note' | 'addendum';
  parentId?: string;
}

function getRpmIdentifierValue(observation: Observation): string | undefined {
  return observation.identifier?.find((identifier) => identifier.system === RPM_MONTH_IDENTIFIER_SYSTEM)?.value;
}

function parseRpmMonthIdentifier(
  identifierValue: string | undefined
): { patientRef: string; month: string; code: RpmObservationCode } | undefined {
  if (!identifierValue) {
    return undefined;
  }
  const [patientRef, month, code] = identifierValue.split('|');
  if (!patientRef || !month || !code) {
    return undefined;
  }
  return { patientRef, month, code: code as RpmObservationCode };
}

function getObservationNumber(observation: Observation): number | undefined {
  return typeof observation.valueInteger === 'number' ? observation.valueInteger : undefined;
}

function getObservationDateTime(observation: Observation): string | undefined {
  return observation.valueDateTime;
}

function getObservationStatus(observation: Observation): string | undefined {
  return observation.valueCodeableConcept?.coding?.[0]?.code;
}

function getObservationText(observation: Observation): string | undefined {
  return observation.valueString ?? observation.note?.[0]?.text;
}

export function buildRpmHistoryRows(observations: Observation[]): RpmHistoryMonthRow[] {
  const byMonth = new Map<string, RpmHistoryMonthRow>();

  for (const observation of observations) {
    const parsed = parseRpmMonthIdentifier(getRpmIdentifierValue(observation));
    if (!parsed) {
      continue;
    }

    const row = byMonth.get(parsed.month) ?? { month: parsed.month };
    switch (parsed.code) {
      case RPM_OBSERVATION_CODES.adherenceNights30Day:
        row.adherenceNights = getObservationNumber(observation);
        break;
      case RPM_OBSERVATION_CODES.lastContactDate:
        row.lastContactDate = getObservationDateTime(observation);
        break;
      case RPM_OBSERVATION_CODES.asyncMonitoringMinutes:
        row.asyncMinutes = getObservationNumber(observation);
        break;
      case RPM_OBSERVATION_CODES.syncContactMinutes:
        row.syncMinutes = getObservationNumber(observation);
        break;
      case RPM_OBSERVATION_CODES.monitoringStatus:
        row.monitoringStatus = getObservationStatus(observation);
        break;
      case RPM_OBSERVATION_CODES.monthlyNote:
        row.monthlyNote = getObservationText(observation);
        break;
    }
    byMonth.set(parsed.month, row);
  }

  return Array.from(byMonth.values()).sort((a, b) => b.month.localeCompare(a.month));
}

function hasRpmNoteCategory(codeableConcept: CodeableConcept | undefined): boolean {
  return Boolean(
    codeableConcept?.coding?.some(
      (coding) =>
        coding.system === RPM_NOTE_COMMUNICATION_SYSTEM &&
        (coding.code === RPM_NOTE_COMMUNICATION_CODES.note || coding.code === RPM_NOTE_COMMUNICATION_CODES.addendum)
    )
  );
}

function getRpmNoteKind(communication: Communication): 'note' | 'addendum' {
  return getStringExtension(communication, RPM_NOTE_EXTENSION_URLS.kind) === 'addendum' ? 'addendum' : 'note';
}

function getBooleanExtension(communication: Communication, url: string): boolean {
  return communication.extension?.find((extension) => extension.url === url)?.valueBoolean === true;
}

function getIntegerExtension(communication: Communication, url: string): number | undefined {
  const value = communication.extension?.find((extension) => extension.url === url)?.valueInteger;
  return typeof value === 'number' ? value : undefined;
}

function getStringExtension(communication: Communication, url: string): string | undefined {
  return communication.extension?.find((extension) => extension.url === url)?.valueString;
}

function getCodeableConceptCode(communication: Communication, url: string): string | undefined {
  return communication.extension?.find((extension) => extension.url === url)?.valueCodeableConcept?.coding?.[0]?.code;
}

export function isRpmNoteCommunication(communication: Communication): boolean {
  return (communication.category ?? []).some(hasRpmNoteCategory);
}

export function getRpmNoteMonth(communication: Communication): string | undefined {
  return getStringExtension(communication, RPM_NOTE_EXTENSION_URLS.month);
}

export function toRpmMonthNoteEntry(communication: Communication): RpmMonthNoteEntry | undefined {
  if (!isRpmNoteCommunication(communication)) {
    return undefined;
  }

  const month = getRpmNoteMonth(communication);
  const text = communication.payload?.find((payload) => typeof payload.contentString === 'string')?.contentString;
  if (!communication.id || !month || !text) {
    return undefined;
  }

  return {
    id: communication.id,
    sent: communication.sent,
    text,
    author: communication.sender?.display ?? communication.sender?.reference ?? 'Unknown sender',
    month,
    asyncReviewed: getBooleanExtension(communication, RPM_NOTE_EXTENSION_URLS.asyncReviewed),
    syncContact: getBooleanExtension(communication, RPM_NOTE_EXTENSION_URLS.syncContact),
    daysReviewed: getIntegerExtension(communication, RPM_NOTE_EXTENSION_URLS.daysReviewed),
    asyncMinutes: getIntegerExtension(communication, RPM_NOTE_EXTENSION_URLS.asyncMinutes),
    asyncActivityType: getCodeableConceptCode(communication, RPM_NOTE_EXTENSION_URLS.asyncActivityType),
    syncMinutes: getIntegerExtension(communication, RPM_NOTE_EXTENSION_URLS.syncMinutes),
    syncMethod: getCodeableConceptCode(communication, RPM_NOTE_EXTENSION_URLS.syncMethod),
    kind: getRpmNoteKind(communication),
    parentId: getStringExtension(communication, RPM_NOTE_EXTENSION_URLS.parent),
  };
}

export function buildRpmNoteCommunication(input: {
  patientId: string;
  patientName: string;
  month: string;
  sent: string;
  sender?: Communication['sender'];
  noteText: string;
  asyncReviewed: boolean;
  daysReviewed?: number;
  asyncMinutes?: number;
  asyncActivityType?: RpmAsyncActivityType;
  syncContact: boolean;
  syncMinutes?: number;
  syncMethod?: RpmSyncContactMethod;
  kind?: 'note' | 'addendum';
  parentId?: string;
}): Communication {
  const extensions: NonNullable<Communication['extension']> = [
    { url: RPM_NOTE_EXTENSION_URLS.month, valueString: input.month },
    { url: RPM_NOTE_EXTENSION_URLS.asyncReviewed, valueBoolean: input.asyncReviewed },
    { url: RPM_NOTE_EXTENSION_URLS.syncContact, valueBoolean: input.syncContact },
    { url: RPM_NOTE_EXTENSION_URLS.kind, valueString: input.kind ?? 'note' },
  ];

  if (input.parentId) {
    extensions.push({ url: RPM_NOTE_EXTENSION_URLS.parent, valueString: input.parentId });
  }

  if (typeof input.daysReviewed === 'number') {
    extensions.push({ url: RPM_NOTE_EXTENSION_URLS.daysReviewed, valueInteger: input.daysReviewed });
  }
  if (typeof input.asyncMinutes === 'number') {
    extensions.push({ url: RPM_NOTE_EXTENSION_URLS.asyncMinutes, valueInteger: input.asyncMinutes });
  }
  if (input.asyncActivityType) {
    extensions.push({
      url: RPM_NOTE_EXTENSION_URLS.asyncActivityType,
      valueCodeableConcept: { coding: [{ system: RPM_NOTE_DETAIL_SYSTEM, code: input.asyncActivityType }] },
    });
  }
  if (typeof input.syncMinutes === 'number') {
    extensions.push({ url: RPM_NOTE_EXTENSION_URLS.syncMinutes, valueInteger: input.syncMinutes });
  }
  if (input.syncMethod) {
    extensions.push({
      url: RPM_NOTE_EXTENSION_URLS.syncMethod,
      valueCodeableConcept: { coding: [{ system: RPM_NOTE_DETAIL_SYSTEM, code: input.syncMethod }] },
    });
  }

  return {
    resourceType: 'Communication',
    status: 'completed',
    subject: { reference: `Patient/${input.patientId}`, display: input.patientName },
    recipient: [{ reference: `Patient/${input.patientId}`, display: input.patientName }],
    sent: input.sent,
    sender: input.sender,
    category: [
      {
        coding: [
          {
            system: RPM_NOTE_COMMUNICATION_SYSTEM,
            code: input.kind === 'addendum' ? RPM_NOTE_COMMUNICATION_CODES.addendum : RPM_NOTE_COMMUNICATION_CODES.note,
            display: input.kind === 'addendum' ? 'RPM Note Addendum' : 'RPM Note',
          },
        ],
        text: 'RPM Note',
      },
    ],
    medium: input.syncMethod
      ? [{ coding: [{ system: RPM_NOTE_DETAIL_SYSTEM, code: input.syncMethod, display: input.syncMethod }] }]
      : undefined,
    extension: extensions,
    payload: [{ contentString: input.noteText }],
  };
}

export function formatSyncRequirement(value: number | undefined): string {
  return typeof value === 'number' ? `${value} min` : '—';
}

export function getTotalMinutesColor(totalMinutes: number): 'green' | 'yellow' | 'red' {
  if (totalMinutes >= 20) {
    return 'green';
  }
  if (totalMinutes >= 10) {
    return 'yellow';
  }
  return 'red';
}
